import './AvatarSelect.scss';

import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import useUploadImage from 'shared/hooks/useUploadImage';

import { ReactComponent as AlertIcon } from 'assets/icons/alert.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';
import cx from 'utils/cx';
import Avatar from './Avatar';
import FakeLink from './FakeLink';

const AvatarSelect = ({ name, required, rules }) => {
  const { t } = useTranslation('common');
  const uploadImageMutation = useUploadImage();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    rules: { required, ...rules },
    defaultValue: '',
  });
  const fileRef = useRef();
  const [localError, setLocalError] = useState(null);

  const onSelectAvatar = () => {
    fileRef.current.click();
  };

  const onAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const extension = file.type.split('/').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png'].indexOf(extension) === -1) {
        setLocalError('JPG, JPEG, PNG 파일만 업로드 가능합니다.');
        return;
      }
      if (file.size / 1024 / 1024 > 10) {
        setLocalError('10MB 이하 파일만 업로드 가능합니다.');
        return;
      }

      setLocalError(null);

      uploadImageMutation.mutate(
        {
          imageData: file,
          type: 'tutor',
        },
        { onSuccess: ({ data }) => field.onChange(data.message) },
      );
    }
  };

  const getAvatarPressable = () => (
    <FakeLink onClick={onSelectAvatar}>
      {field.value ? (
        <>
          <Avatar size={154} src={field.value} />
          <div className="overlay" />
        </>
      ) : (
        <>
          <p>{t('avatarFormat')}</p>
          <UploadIcon />
        </>
      )}
    </FakeLink>
  );

  const getTip = () => (
    <p className="appropriate-picture">
      <Trans i18nKey="common:appropriatePicture" />
    </p>
  );

  const getErrorMessage = () => (
    <div className="form-message-wrapper">
      <AlertIcon />
      <span>{localError || t('isRequired')}</span>
    </div>
  );

  return (
    <>
      <div className={cx('avatar-select', { selected: field.value })}>
        <input
          ref={field.ref}
          value={field.value || ''}
          type="text"
          readOnly
          className="ref-hidden"
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/png, image/jpeg, image/jpg"
          onChange={onAvatarChange}
        />
        {getAvatarPressable()}
      </div>
      {getTip()}
      {(error || localError) && getErrorMessage()}
    </>
  );
};

AvatarSelect.propTypes = {
  name: PropTypes.string,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rules: PropTypes.object,
};

AvatarSelect.defaultProps = {
  name: '',
  required: true,
  rules: {},
};

export default AvatarSelect;
